import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, BookOpen, List, FolderOpen, DollarSign, Star } from 'lucide-react';
import PricingPlanManager from './PricingPlanManager';

const CourseManagement = () => {
  const [courses, setCourses] = useState([]);
  const [selectedCourseForPricing, setSelectedCourseForPricing] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [subjects, setSubjects] = useState({});
  const [chapters, setChapters] = useState({});
  const [loading, setLoading] = useState(false);
  const [showCourseDialog, setShowCourseDialog] = useState(false);
  const [showSubjectDialog, setShowSubjectDialog] = useState(false);
  const [showChapterDialog, setShowChapterDialog] = useState(false);
  const [courseForm, setCourseForm] = useState({ id: null, name: '', description: '', category: 'General', department_ids: [], price: 0, thumbnail: '', enabled: true });
  const [subjectForm, setSubjectForm] = useState({ id: null, course_ids: [], name: '', order: 1 });
  const [chapterForm, setChapterForm] = useState({ id: null, subject_id: '', name: '', order: 1 });

  useEffect(() => { 
    fetchCourses(); 
    fetchDepartments();
  }, []);

  const fetchCourses = async () => {
    try {
      const response = await axios.get('/courses');
      setCourses(response.data);
      for (const course of response.data) { fetchSubjects(course.id); }
    } catch (error) { console.error('Failed to fetch courses:', error); }
  };

  const fetchDepartments = async () => {
    try {
      const response = await axios.get('/departments');
      setDepartments(response.data);
    } catch (error) { console.error('Failed to fetch departments:', error); }
  };

  const fetchSubjects = async (courseId) => {
    try {
      const response = await axios.get(`/subjects/${courseId}`);
      setSubjects(prev => ({ ...prev, [courseId]: response.data }));
      for (const subject of response.data) { fetchChapters(subject.id); }
    } catch (error) { console.error('Failed to fetch subjects:', error); }
  };

  const fetchChapters = async (subjectId) => {
    try {
      const response = await axios.get(`/chapters/${subjectId}`);
      setChapters(prev => ({ ...prev, [subjectId]: response.data }));
    } catch (error) { console.error('Failed to fetch chapters:', error); }
  };

  const handleSaveCourse = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (courseForm.id) {
        await axios.put(`/admin/courses/${courseForm.id}`, courseForm);
        toast.success('Course updated');
      } else {
        await axios.post('/admin/courses', courseForm);
        toast.success('Course created');
      }
      setShowCourseDialog(false);
      setCourseForm({ id: null, name: '', description: '', category: 'General', department_ids: [], price: 0, thumbnail: '', enabled: true });
      fetchCourses();
    } catch (error) { toast.error('Failed to save course'); }
    finally { setLoading(false); }
  };

  const handleDeleteCourse = async (courseId) => {
    if (!window.confirm('Delete this course?')) return;
    try { await axios.delete(`/admin/courses/${courseId}`); toast.success('Course deleted'); fetchCourses(); }
    catch (error) { toast.error('Failed to delete course'); }
  };

  const handleSaveSubject = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (subjectForm.id) {
        await axios.put(`/admin/subjects/${subjectForm.id}`, subjectForm);
        toast.success('Subject updated');
      } else {
        await axios.post('/admin/subjects', subjectForm);
        toast.success('Subject created');
      }
      setShowSubjectDialog(false);
      setSubjectForm({ id: null, course_ids: [], name: '', order: 1 });
      // Refresh subjects for all assigned courses
      if (subjectForm.course_ids && subjectForm.course_ids.length > 0) {
        subjectForm.course_ids.forEach(courseId => fetchSubjects(courseId));
      }
      fetchCourses();
    } catch (error) { toast.error('Failed to save subject'); }
    finally { setLoading(false); }
  };

  const handleDeleteSubject = async (subjectId, courseId) => {
    if (!window.confirm('Delete this subject?')) return;
    try { await axios.delete(`/admin/subjects/${subjectId}`); toast.success('Subject deleted'); fetchSubjects(courseId); }
    catch (error) { toast.error('Failed to delete subject'); }
  };

  const handleSaveChapter = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (chapterForm.id) {
        await axios.put(`/admin/chapters/${chapterForm.id}`, chapterForm);
        toast.success('Chapter updated');
      } else {
        await axios.post('/admin/chapters', chapterForm);
        toast.success('Chapter created');
      }
      setShowChapterDialog(false);
      setChapterForm({ id: null, subject_id: '', name: '', order: 1 });
      fetchChapters(chapterForm.subject_id);
    } catch (error) { toast.error('Failed to save chapter'); }
    finally { setLoading(false); }
  };

  const handleDeleteChapter = async (chapterId, subjectId) => {
    if (!window.confirm('Delete this chapter?')) return;
    try { await axios.delete(`/admin/chapters/${chapterId}`); toast.success('Chapter deleted'); fetchChapters(subjectId); }
    catch (error) { toast.error('Failed to delete chapter'); }
  };

  const handleToggleFeatured = async (courseId, currentStatus) => {
    // Check if we're trying to add and already have 4 featured courses
    const featuredCount = courses.filter(c => c.is_featured).length;
    if (!currentStatus && featuredCount >= 4) {
      toast.error('Maximum 4 popular courses allowed. Remove one first.');
      return;
    }
    
    try {
      await axios.put(`/admin/courses/${courseId}/featured`, { 
        is_featured: !currentStatus,
        featured_order: 0 
      });
      toast.success(currentStatus ? 'Removed from Popular Courses' : 'Added to Popular Courses');
      fetchCourses();
    } catch (error) { 
      toast.error('Failed to update featured status'); 
    }
  };

  // Get featured courses for the management section
  const featuredCourses = courses.filter(c => c.is_featured);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-800">Course Management</h2>
          <p className="text-sm text-gray-600 mt-1">Manage courses, subjects, and chapters</p>
        </div>
        <Button onClick={() => { setCourseForm({ id: null, name: '', description: '', category: 'General', department_ids: [], price: 0, thumbnail: '', enabled: true }); setShowCourseDialog(true); }} className="bg-blue-600 hover:bg-blue-700 text-sm">
          <Plus className="w-4 h-4 mr-2" /> Add Course
        </Button>
      </div>

      {/* Popular Courses Management Section */}
      <Card className="border-yellow-200 bg-gradient-to-r from-yellow-50 to-orange-50">
        <CardHeader className="pb-2 sm:pb-4">
          <CardTitle className="flex items-center justify-between text-base sm:text-lg">
            <div className="flex items-center gap-2">
              <Star className="w-5 h-5 text-yellow-600 fill-yellow-400" />
              <span>Popular Courses on Landing Page</span>
            </div>
            <span className={`text-sm font-normal px-2 py-1 rounded-full ${featuredCourses.length >= 4 ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
              {featuredCourses.length}/4
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {featuredCourses.length === 0 ? (
            <p className="text-sm text-gray-600 text-center py-4">
              No popular courses selected. Click the <Star className="w-4 h-4 inline mx-1" /> icon on any course below to add it.
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
              {featuredCourses.map((course) => (
                <div 
                  key={course.id} 
                  className="flex items-center justify-between p-2 sm:p-3 bg-white rounded-lg border border-yellow-200 shadow-sm"
                >
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center flex-shrink-0">
                      {course.thumbnail ? (
                        <img src={course.thumbnail} alt="" className="w-full h-full object-cover rounded-lg" />
                      ) : (
                        <BookOpen className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-xs sm:text-sm truncate">{course.name}</p>
                      <p className="text-xs text-gray-500">₹{course.price}</p>
                    </div>
                  </div>
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    className="h-8 w-8 p-0 text-red-500 hover:bg-red-50 hover:text-red-700 flex-shrink-0"
                    onClick={() => handleToggleFeatured(course.id, true)}
                    title="Remove from Popular"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
          {featuredCourses.length > 0 && featuredCourses.length < 4 && (
            <p className="text-xs text-gray-500 mt-3 text-center">
              You can add {4 - featuredCourses.length} more course{4 - featuredCourses.length > 1 ? 's' : ''} to Popular list
            </p>
          )}
        </CardContent>
      </Card>

      {/* Course List with Collapsible Structure */}
      <Accordion type="multiple" className="space-y-3">
        {courses.map((course) => (
          <AccordionItem key={course.id} value={course.id} className={`border rounded-lg overflow-hidden ${!course.enabled ? 'opacity-60' : ''}`}>
            <AccordionTrigger className="hover:no-underline px-3 sm:px-4 py-2 sm:py-3 bg-gradient-to-r from-blue-50 to-purple-50 [&[data-state=open]]:rounded-b-none">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between w-full gap-2">
                {/* Course Info */}
                <div className="flex items-start gap-2 flex-1 min-w-0">
                  <BookOpen className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-left min-w-0 flex-1">
                    <div className="font-semibold text-sm sm:text-base leading-tight">{course.name}</div>
                    <div className="flex flex-wrap gap-1 mt-1">
                      <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded font-semibold">₹{course.price}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded ${course.enabled ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {course.enabled ? 'On' : 'Off'}
                      </span>
                      {course.is_featured && (
                        <span className="text-xs bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded flex items-center gap-0.5">
                          <Star className="w-3 h-3 fill-yellow-500" /> Popular
                        </span>
                      )}
                      <span className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">
                        {subjects[course.id]?.length || 0} subj
                      </span>
                    </div>
                  </div>
                </div>
                {/* Action buttons - Always visible */}
                <div className="flex gap-0.5 sm:gap-1 flex-shrink-0 self-end sm:self-center" onClick={(e) => e.stopPropagation()}>
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    className={`h-7 w-7 p-0 ${course.is_featured ? 'text-yellow-600 bg-yellow-50' : ''}`} 
                    onClick={() => handleToggleFeatured(course.id, course.is_featured)} 
                    title={course.is_featured ? 'Remove' : 'Popular'}
                  >
                    <Star className={`w-4 h-4 ${course.is_featured ? 'fill-yellow-500' : ''}`} />
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => setSelectedCourseForPricing(course)} title="Pricing">
                    <DollarSign className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => { setSubjectForm({ id: null, course_ids: [course.id], name: '', order: (subjects[course.id]?.length || 0) + 1 }); setShowSubjectDialog(true); }} title="Add Subject">
                    <Plus className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => { setCourseForm(course); setShowCourseDialog(true); }} title="Edit">
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-600" onClick={() => handleDeleteCourse(course.id)} title="Delete">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-4 pt-2 bg-white">
              {subjects[course.id] && subjects[course.id].length > 0 ? (
                <Accordion type="single" collapsible className="w-full space-y-2">
                  {subjects[course.id].map((subject) => (
                    <AccordionItem key={subject.id} value={subject.id} className="border rounded-lg bg-gray-50">
                      <AccordionTrigger className="hover:no-underline px-3 py-2">
                        <div className="flex items-center justify-between w-full pr-2">
                          <div className="flex items-center gap-2">
                            <List className="w-4 h-4 text-blue-700" />
                            <span className="font-medium text-sm">{subject.order}. {subject.name}</span>
                            <span className="text-xs text-gray-500">({chapters[subject.id]?.length || 0} chapters)</span>
                          </div>
                          <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                            <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => { setChapterForm({ id: null, subject_id: subject.id, name: '', order: (chapters[subject.id]?.length || 0) + 1 }); setShowChapterDialog(true); }} title="Add Chapter">
                              <Plus className="w-3 h-3" />
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => { setSubjectForm(subject); setShowSubjectDialog(true); }} title="Edit Subject">
                              <Edit className="w-3 h-3" />
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7 px-2 text-red-600 hover:text-red-700" onClick={() => handleDeleteSubject(subject.id, course.id)} title="Delete Subject">
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="px-3 pb-3">
                        <div className="ml-6 space-y-2 mt-1">
                          {chapters[subject.id] && chapters[subject.id].length > 0 ? (
                            chapters[subject.id].map((chapter) => (
                              <div key={chapter.id} className="flex items-center justify-between p-2 border rounded bg-white hover:bg-gray-50">
                                <div className="flex items-center gap-2">
                                  <FolderOpen className="w-3.5 h-3.5 text-blue-600" />
                                  <span className="text-sm">{chapter.order}. {chapter.name}</span>
                                </div>
                                <div className="flex gap-1">
                                  <Button size="sm" variant="ghost" className="h-6 px-1.5" onClick={() => { setChapterForm(chapter); setShowChapterDialog(true); }}>
                                    <Edit className="w-3 h-3" />
                                  </Button>
                                  <Button size="sm" variant="ghost" className="h-6 px-1.5 text-red-600" onClick={() => handleDeleteChapter(chapter.id, subject.id)}>
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                </div>
                              </div>
                            ))
                          ) : (
                            <p className="text-xs text-gray-500 text-center py-2">No chapters yet</p>
                          )}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              ) : (
                <p className="text-sm text-gray-500 text-center py-3">No subjects yet. Click + to add one.</p>
              )}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
      
      {courses.length === 0 && (
        <Card><CardContent className="py-12 text-center text-gray-500"><BookOpen className="w-12 h-12 mx-auto mb-4 text-gray-400" /><p>No courses yet</p></CardContent></Card>
      )}

      <Dialog open={showCourseDialog} onOpenChange={setShowCourseDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{courseForm.id ? 'Edit Course' : 'Create Course'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveCourse} className="space-y-4">
            {/* Course Name */}
            <div>
              <label className="text-sm font-medium">Course Name *</label>
              <Input 
                value={courseForm.name} 
                onChange={(e) => setCourseForm({ ...courseForm, name: e.target.value })} 
                placeholder="Enter course name"
                required 
              />
            </div>
            
            {/* Description */}
            <div>
              <label className="text-sm font-medium">Description *</label>
              <Textarea 
                value={courseForm.description} 
                onChange={(e) => setCourseForm({ ...courseForm, description: e.target.value })} 
                rows={2} 
                placeholder="Brief description of the course"
                required 
              />
            </div>
            
            {/* Category & Price - Side by side on desktop, stacked on mobile */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium">Category</label>
                <Input 
                  value={courseForm.category} 
                  onChange={(e) => setCourseForm({ ...courseForm, category: e.target.value })}
                  placeholder="e.g., General, Advanced"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Price (₹) *</label>
                <Input 
                  type="number" 
                  value={courseForm.price} 
                  onChange={(e) => setCourseForm({ ...courseForm, price: parseFloat(e.target.value) })} 
                  placeholder="0"
                  required 
                />
              </div>
            </div>
            
            {/* Thumbnail - Compact version */}
            <div>
              <label className="text-sm font-medium">Thumbnail</label>
              <div className="flex flex-col sm:flex-row gap-3 items-start">
                <div className="flex-1 w-full">
                  <Input 
                    type="file" 
                    accept="image/*"
                    className="text-sm"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        const reader = new FileReader();
                        reader.onloadend = () => {
                          setCourseForm({ ...courseForm, thumbnail: reader.result });
                        };
                        reader.readAsDataURL(file);
                      }
                    }}
                  />
                  <Input 
                    value={courseForm.thumbnail || ''} 
                    onChange={(e) => setCourseForm({ ...courseForm, thumbnail: e.target.value })} 
                    placeholder="Or paste image URL"
                    className="mt-2 text-sm"
                  />
                </div>
                {courseForm.thumbnail && (
                  <img 
                    src={courseForm.thumbnail} 
                    alt="Preview" 
                    className="w-16 h-16 object-cover rounded border flex-shrink-0"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                )}
              </div>
            </div>
            
            {/* Department Selection - Compact */}
            <div>
              <label className="text-sm font-medium block mb-1">Departments</label>
              <div className="border rounded-lg p-3 max-h-32 overflow-y-auto bg-gray-50">
                {departments.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {departments.map((dept) => (
                      <div key={dept.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={`dept-${dept.id}`}
                          checked={courseForm.department_ids?.includes(dept.id)}
                          onCheckedChange={(checked) => {
                            const currentDeptIds = courseForm.department_ids || [];
                            if (checked) {
                              setCourseForm({ ...courseForm, department_ids: [...currentDeptIds, dept.id] });
                            } else {
                              setCourseForm({ ...courseForm, department_ids: currentDeptIds.filter(id => id !== dept.id) });
                            }
                          }}
                        />
                        <label htmlFor={`dept-${dept.id}`} className="text-xs cursor-pointer truncate">
                          {dept.name}
                        </label>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-gray-500">No departments available</p>
                )}
              </div>
              {courseForm.department_ids?.length > 0 && (
                <p className="text-xs text-green-600 mt-1">
                  {courseForm.department_ids.length} department(s) selected
                </p>
              )}
            </div>
            
            {/* Enable Switch & Actions */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-2 border-t">
              <div className="flex items-center gap-2">
                <Switch 
                  checked={courseForm.enabled} 
                  onCheckedChange={(checked) => setCourseForm({ ...courseForm, enabled: checked })} 
                />
                <label className="text-sm">Enable Course</label>
              </div>
              <div className="flex gap-2">
                <Button type="button" variant="outline" size="sm" onClick={() => setShowCourseDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit" size="sm" disabled={loading}>
                  {loading ? 'Saving...' : 'Save Course'}
                </Button>
              </div>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={showSubjectDialog} onOpenChange={setShowSubjectDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{subjectForm.id ? 'Edit Subject' : 'Create Subject'}</DialogTitle></DialogHeader>
          <form onSubmit={handleSaveSubject} className="space-y-4">
            <div><label className="text-sm font-medium">Subject Name</label><Input value={subjectForm.name} onChange={(e) => setSubjectForm({ ...subjectForm, name: e.target.value })} required /></div>
            
            {/* Multiple Course Selection */}
            <div>
              <label className="text-sm font-medium block mb-2">Assign to Courses</label>
              <div className="border rounded-lg p-4 space-y-2 max-h-48 overflow-y-auto">
                {courses.length > 0 ? (
                  courses.map((course) => (
                    <div key={course.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`course-${course.id}`}
                        checked={subjectForm.course_ids?.includes(course.id)}
                        onCheckedChange={(checked) => {
                          const currentCourseIds = subjectForm.course_ids || [];
                          if (checked) {
                            setSubjectForm({ ...subjectForm, course_ids: [...currentCourseIds, course.id] });
                          } else {
                            setSubjectForm({ ...subjectForm, course_ids: currentCourseIds.filter(id => id !== course.id) });
                          }
                        }}
                      />
                      <label htmlFor={`course-${course.id}`} className="text-sm cursor-pointer">
                        {course.name}
                      </label>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-500">No courses available. Create courses first.</p>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {subjectForm.course_ids?.length > 0 
                  ? `Assigned to ${subjectForm.course_ids.length} course(s)`
                  : 'Select at least one course'}
              </p>
            </div>
            
            <div><label className="text-sm font-medium">Order</label><Input type="number" value={subjectForm.order} onChange={(e) => setSubjectForm({ ...subjectForm, order: parseInt(e.target.value) })} min="1" required /></div>
            <div className="flex gap-3 justify-end">
              <Button type="button" variant="outline" onClick={() => setShowSubjectDialog(false)}>Cancel</Button>
              <Button type="submit" disabled={loading}>{loading ? 'Saving...' : 'Save'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={showChapterDialog} onOpenChange={setShowChapterDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{chapterForm.id ? 'Edit Chapter' : 'Create Chapter'}</DialogTitle></DialogHeader>
          <form onSubmit={handleSaveChapter} className="space-y-4">
            <div><label className="text-sm font-medium">Chapter Name</label><Input value={chapterForm.name} onChange={(e) => setChapterForm({ ...chapterForm, name: e.target.value })} required /></div>
            <div><label className="text-sm font-medium">Order</label><Input type="number" value={chapterForm.order} onChange={(e) => setChapterForm({ ...chapterForm, order: parseInt(e.target.value) })} min="1" required /></div>
            <div className="flex gap-3 justify-end">
              <Button type="button" variant="outline" onClick={() => setShowChapterDialog(false)}>Cancel</Button>
              <Button type="submit" disabled={loading}>{loading ? 'Saving...' : 'Save'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Pricing Plans Dialog */}
      <Dialog open={!!selectedCourseForPricing} onOpenChange={() => setSelectedCourseForPricing(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Pricing Plans - {selectedCourseForPricing?.name}</DialogTitle>
          </DialogHeader>
          {selectedCourseForPricing && (
            <PricingPlanManager 
              courseId={selectedCourseForPricing.id} 
              onClose={() => setSelectedCourseForPricing(null)}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CourseManagement;